import React, { useState } from 'react';
import { Card, Alert, Button } from 'react-bootstrap';
import TeamBreadcrumb from './TeamBreadcrumb';
import TeamTable from './TeamTable';
import { NetworkUser } from './hooks/use-network';
import { apiClient } from '../../services/api';

interface NetworkViewProps {
  teamMembers: NetworkUser[];
  visitedUsers: NetworkUser[];
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  currentUserId?: string | undefined;
  navigationHistory?: string[];
  currentLevel?: number;
  onViewNetwork: (memberId: string) => void;
  onRefresh?: () => void;
  onBackToMyNetwork?: () => void;
  onNavigateToLevel: (userId: string | null, levelIndex: number) => void;
  onShowVisualization?: () => void;
}

const NetworkView: React.FC<NetworkViewProps> = ({
  teamMembers,
  visitedUsers,
  isLoading = false,
  isError = false,
  error = null,
  currentUserId,
  currentLevel = 1,
  navigationHistory = [],
  onViewNetwork,
  onRefresh,
  onBackToMyNetwork,
  onNavigateToLevel,
  onShowVisualization
}) => {
  const getVisitedUserById = (id: string): NetworkUser | undefined => {
    return visitedUsers.find(user => user.id === id);
  };

  const [downloadingUser, setDownloadingUser] = useState(false);

  const handleDownload = async () => {
    try {
      setDownloadingUser(true);

      const response = await apiClient.get(
        `/users/network/tree${currentUserId ? `?user_id=${encodeURIComponent(currentUserId)}` : ""}`,
        {
          responseType: "blob",
          headers: {
            Authorization: localStorage.getItem("token") || "",
          },
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement("a");
      link.href = url;
      link.download = "network.xlsx";

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloadingUser(false);
    }
  };



  return (
    <Card className="border-0 rounded-3 p-4 mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h4 className="mb-0">
          Miembros del Equipo - {currentUserId && currentLevel > 1
            ? `Red de Usuario ${getVisitedUserById(currentUserId)?.name} (Nivel ${currentLevel})`
            : `Mi Red`
          }
        </h4>
        <div className="d-flex gap-2">
          {currentUserId && currentLevel > 1 && onBackToMyNetwork && (
            <Button variant="outline-primary" size="sm" onClick={onBackToMyNetwork}>
              <i className="ri-arrow-left-line me-1"></i>
              Volver a Mi Red
            </Button>
          )}
          {onShowVisualization && (
            <Button variant="outline-success" size="sm" onClick={onShowVisualization}>
              <i className="ri-node-tree me-1"></i>
              Ver Gráfica
            </Button>
          )}
          {onShowVisualization && (
            <Button
              variant="success"
              size="sm"
              className="d-flex align-items-center gap-2"
              disabled={downloadingUser}
              title={`Descargar la red de ${getVisitedUserById(currentUserId ?? '')?.name}`}
              onClick={() => handleDownload()}
            >
              {downloadingUser ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span>Generando...</span>
                </>
              ) : (
                <>
                  <i className="ri-download-2-line"></i>
                  <span>Descargar red</span>
                </>
              )}
            </Button>
          )}
          {onRefresh && (
            <Button variant="outline-secondary" size="sm" onClick={onRefresh}>
              <i className="ri-refresh-line me-1"></i>
              Actualizar
            </Button>
          )}
        </div>
      </div>

      {isError && (
        <Alert variant="danger" className="mb-3">
          <Alert.Heading>Error al cargar la red</Alert.Heading>
          <p className="mb-0">
            {error?.message || 'Ocurrió un error al cargar los datos de la red.'}
          </p>
        </Alert>
      )}

      <TeamBreadcrumb
        navigationHistory={navigationHistory}
        visitedUsers={visitedUsers}
        onNavigate={onNavigateToLevel}
      />

      <div className="default-table-area">
        <TeamTable
          isLoading={isLoading}
          teamMembers={teamMembers}
          onViewNetwork={onViewNetwork}
          currentUserId={currentUserId}
        />
      </div>
    </Card>
  );
};

export default NetworkView;
